import { Link } from "react-router";
import type { DailyPulse } from "@altair/shared/types";

/**
 * Older / Newer issue links at the bottom of a pulse page. Extracted from
 * InsightDetailPage.tsx to keep that file under the 400-line architectural
 * limit — behavior and styling are unchanged.
 *
 * Links use `reloadDocument` (full page load): each /news/:date is SSR'd by the
 * news-renderer with its own pulse data, so client-side routing would render
 * the target date from the current page's payload and 404.
 */

interface PulseNavigationProps {
  currentDate: string;
  /** Recent pulse dates (newest first); only `.date` is read. */
  allPulses: Pick<DailyPulse, "date">[];
}

export function PulseNavigation({
  currentDate,
  allPulses,
}: PulseNavigationProps) {
  const currentIdx = allPulses.findIndex((p) => p.date === currentDate);
  const prev = currentIdx > 0 ? allPulses[currentIdx - 1] : null;
  const next =
    currentIdx >= 0 && currentIdx < allPulses.length - 1
      ? allPulses[currentIdx + 1]
      : null;

  if (!prev && !next) return null;

  return (
    <div
      className="flex items-center justify-between"
      style={{
        marginTop: 56,
        paddingTop: 24,
        borderTop: "1px solid hsl(var(--foreground) / 0.08)",
      }}
    >
      {next ? (
        <Link
          to={`/news/${next.date}`}
          reloadDocument
          className="no-underline"
          style={{
            fontSize: 13,
            color: "hsl(var(--foreground) / 0.45)",
            transition: "color 150ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "hsl(var(--foreground))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "hsl(var(--foreground) / 0.45)";
          }}
        >
          ← Older issue
        </Link>
      ) : (
        <span />
      )}
      {prev ? (
        <Link
          to={`/news/${prev.date}`}
          reloadDocument
          className="no-underline"
          style={{
            fontSize: 13,
            color: "hsl(var(--foreground) / 0.45)",
            transition: "color 150ms",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "hsl(var(--foreground))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "hsl(var(--foreground) / 0.45)";
          }}
        >
          Newer issue →
        </Link>
      ) : (
        <span />
      )}
    </div>
  );
}
